import React, { useState } from 'react';
import { Search, Plus, Trash2, Image } from 'lucide-react';
import { motion } from 'framer-motion';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import { useTranslation } from 'react-i18next';

const CardBuilder = () => {
  const { t } = useTranslation();
  const [cardList, setCardList] = useState('');
  const [selectedCards, setSelectedCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search Scryfall API for UNIQUE ART (not just prints)
  const searchCards = async (query) => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      // Use unique=art to get different artworks, not just different printings
      const response = await fetch(`https://api.scryfall.com/cards/search?q=${encodeURIComponent(query)}&unique=art&order=released`);
      const data = await response.json();
      
      if (data.data) {
        setSearchResults(data.data.slice(0, 20)); // Show first 20 art variations
      }
    } catch (error) {
      console.error('Error searching cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchCards(searchQuery);
  };

  const addCard = (card) => {
    setSelectedCards([...selectedCards, {
      id: card.id,
      name: card.name,
      image: card.image_uris?.normal || card.card_faces?.[0]?.image_uris?.normal,
      set: card.set_name,
      setCode: card.set,
      collectorNumber: card.collector_number
    }]);
    setSearchQuery('');
    setSearchResults([]);
  };

  const removeCard = (index) => {
    setSelectedCards(selectedCards.filter((_, i) => i !== index));
  };

  const parseTextList = () => {
    const lines = cardList.split('\n').filter(line => line.trim());
    const parsed = lines.map(line => {
      const match = line.match(/^(\d+)x?\s+(.+)$/i);
      if (match) {
        return { quantity: parseInt(match[1]), name: match[2].trim() };
      }
      return { quantity: 1, name: line.trim() };
    });
    
    // Auto-search for first card as example
    if (parsed.length > 0) {
      searchCards(parsed[0].name);
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black pt-24 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl font-outfit font-bold tracking-tight text-white mb-4">
              <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">
                Card Builder
              </span>
            </h1>
            <p className="text-white/70 text-xl">Build your deck and choose card versions</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Text Input */}
            <motion.div
              className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-2xl font-outfit font-bold text-white mb-4 flex items-center">
                <Image size={24} className="mr-2 text-violet-400" />
                Paste Your Decklist
              </h2>
              <p className="text-white/60 text-sm mb-4">
                Enter card names, one per line. Format: "4x Lightning Bolt" or just "Lightning Bolt"
              </p>
              
              <textarea
                value={cardList}
                onChange={(e) => setCardList(e.target.value)}
                placeholder="4x Lightning Bolt\n1x Black Lotus\n4x Island\n..."
                className="w-full h-96 bg-black/60 border border-white/20 text-white rounded-xl px-4 py-3 font-mono text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500 transition-all placeholder:text-zinc-600 resize-none"
              />
              
              <button
                onClick={parseTextList}
                className="mt-4 w-full px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl font-bold hover:from-violet-500 hover:to-purple-500 transition-all shadow-lg"
              >
                Parse List & Find Cards
              </button>
            </motion.div>

            {/* Right: Card Search */}
            <motion.div
              className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h2 className="text-2xl font-outfit font-bold text-white mb-4 flex items-center">
                <Search size={24} className="mr-2 text-violet-400" />
                Search Individual Cards
              </h2>
              
              <form onSubmit={handleSearch} className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for a card..."
                    className="w-full bg-black/60 border border-white/20 text-white rounded-xl px-4 py-3 pr-12 focus:border-violet-500 focus:ring-2 focus:ring-violet-500 transition-all placeholder:text-zinc-600"
                  />
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 bg-violet-600 text-white rounded-lg hover:bg-violet-500 transition-all"
                  >
                    <Search size={20} />
                  </button>
                </div>
              </form>

              {/* Search Results */}
              {loading && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-600 mx-auto"></div>
                  <p className="text-white/60 mt-4">Searching Scryfall...</p>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                  {searchResults.map((card) => (
                    <motion.div
                      key={card.id}
                      className="flex items-center space-x-3 bg-black/60 border border-white/10 rounded-xl p-3 hover:border-violet-500 transition-all group cursor-pointer"
                      onClick={() => addCard(card)}
                      whileHover={{ scale: 1.02 }}
                    >
                      <img 
                        src={card.image_uris?.small || card.card_faces?.[0]?.image_uris?.small}
                        alt={card.name}
                        className="w-16 h-22 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="text-white font-semibold">{card.name}</h3>
                        <p className="text-white/60 text-sm">{card.set_name} • {card.collector_number}</p>
                      </div>
                      <Plus size={20} className="text-violet-400 group-hover:scale-125 transition-transform" />
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Selected Cards */}
          {selectedCards.length > 0 && (
            <motion.div
              className="mt-8 bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-2xl font-outfit font-bold text-white mb-6">
                Your Deck ({selectedCards.length} cards)
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {selectedCards.map((card, index) => (
                  <motion.div
                    key={index}
                    className="relative group"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <img 
                      src={card.image}
                      alt={card.name}
                      className="w-full rounded-lg shadow-lg hover:scale-105 transition-transform"
                    />
                    <button
                      onClick={() => removeCard(index)}
                      className="absolute -top-2 -right-2 p-2 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500 shadow-lg"
                    >
                      <Trash2 size={16} />
                    </button>
                    <div className="mt-2 text-center">
                      <p className="text-white text-xs font-semibold truncate">{card.name}</p>
                      <p className="text-white/60 text-xs">{card.setCode.toUpperCase()}</p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="mt-8 flex justify-between items-center">
                <div className="text-white">
                  <p className="text-sm text-white/60">Total Cards</p>
                  <p className="text-3xl font-bold">{selectedCards.length}</p>
                </div>
                <button className="px-8 py-4 bg-gradient-to-r from-violet-600 to-pink-600 text-white rounded-xl font-bold text-lg hover:from-violet-500 hover:to-pink-500 transition-all shadow-lg hover:scale-105">
                  Add to Cart
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CardBuilder;
